import { useMemo } from 'react'
import dayjs from 'dayjs'

import PhoneOutlinedIcon from '@ant-design/icons/PhoneOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import AccessTimeOutlinedIcon from '@icons/AccessTimeOutlined'
import EnvironmentIcon from '@icons/Environment'
import GlobalIcon from '@icons/Global'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import { getPhoneFormatted } from '@shared/utils'

import Section from '@components/Details/Section'

import Field from './Field'

export default function Overview({ cbo, ruleSets, isLoading }) {
  const partners = useMemo(() => {
    const result = (ruleSets?.prescriptionPartners || [])
      .filter((p) => p.active)
      .map((p) => `${p.partner_name}, since ${dayjs(p.start_date, 'MM/DD/YYYY').format('L')}`)

    if (result.length === 0) return 'No'
    return (
      <Stack>
        {result.map((p) => (
          <Typography key={p}>{p}</Typography>
        ))}
      </Stack>
    )
  }, [ruleSets])

  const insurancePartner = cbo?.americanExchangePartner || cbo?.prideLifePartner

  return (
    <Section title="Overview" icon={<InfoOutlined />}>
      <Stack sx={{ gap: 2 }}>
        {isLoading ? (
          <Typography>
            <Skeleton width="60%" />
          </Typography>
        ) : (
          <Stack direction="row" sx={styles.contacts}>
            {cbo.phone && <Contact icon={<PhoneOutlinedIcon />}>{getPhoneFormatted(cbo.phone)}</Contact>}
            {(cbo.city || cbo.state) && (
              <Contact icon={<EnvironmentIcon fontSize="inherit" />}>{[cbo.city, cbo.state].filter(Boolean).join(', ')}</Contact>
            )}
            {cbo.url && (
              <Contact icon={<GlobalIcon fontSize="inherit" />}>
                <Link href={cbo.url} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                  {cbo.url}
                </Link>
              </Contact>
            )}
            {cbo.timezone && <Contact icon={<AccessTimeOutlinedIcon fontSize="inherit" />}>{cbo.timezone}</Contact>}
          </Stack>
        )}
        <Stack sx={{ gap: 1 }}>
          <Field label="Date Joined" isLoading={isLoading} value={cbo?.startDate ? dayjs(cbo.startDate).format('L') : ''} />
          <Field label="Hours" isLoading={isLoading} value={cbo?.hoursOfOperation} />
          <Field label="Medical PC" isLoading={isLoading} value={cbo?.medicalPc} />
          <Field label="Accepting New Patients" isLoading={isLoading} value={cbo?.acceptingNewPatients ? 'Yes' : 'No'} />
          <Field label="Allow Uninsured" isLoading={isLoading} value={cbo?.cboPreventUninsured ? 'No' : 'Yes'} />
          <Field label="Maintains Uninsured Patients" isLoading={isLoading} value={cbo?.maintainsUninsuredPatients ? 'Yes' : 'No'} />
          {insurancePartner ? (
            <Field
              label={cbo.prideLifePartner ? 'Allow PrideLife' : 'Allow American Exchange'}
              value={
                <Stack>
                  <Typography>Yes</Typography>
                  <Link href={cbo.cboInsurancePartnerLink} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                    {cbo.cboInsurancePartnerLink}
                  </Link>
                </Stack>
              }
            />
          ) : (
            <Field label="Insurance Partner" isLoading={isLoading} value="No" />
          )}
          <Field label="Receiving Q Care Files" isLoading={isLoading} value={partners} />
        </Stack>
      </Stack>
    </Section>
  )
}

function Contact({ icon, children }) {
  return (
    <Stack direction="row" sx={styles.contact}>
      {icon}
      <Typography component="div">{children}</Typography>
    </Stack>
  )
}

const styles = {
  contacts: {
    flexWrap: 'wrap',
    columnGap: 3,
    rowGap: 1,
  },
  contact: {
    gap: 1,
    alignItems: 'center',
    minWidth: 0,
  },
}
